import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, TrendingUp, Building, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const AreasSection = () => {
  const areas = [
    {
      id: 'dubai-south',
      name: 'Dubai South',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/dubai_south_wmclrt.png',
      description: 'Future-ready smart city with unmatched growth potential near Expo 2020 site',
      highlights: ['Expo Legacy', 'Al Maktoum Airport', 'High ROI'],
    },
    {
      id: 'furjan',
      name: 'Furjan',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/Furjan_cn9ehj.png',
      description: 'Family-oriented community with modern villas and townhouses',
      highlights: ['Family Living', 'Green Spaces', 'Premium Villas'],
    },
    {
      id: 'wasl-gate',
      name: 'Wasl Gate',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/wasl_gate_vbri7v.png',
      description: 'Ultra-modern development in the heart of Dubai with luxury finishes',
      highlights: ['Central Location', 'Luxury Finish', 'Prime Investment'],
    },
    {
      id: 'jbr',
      name: 'JBR',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/JBR_trjpor.png',
      description: 'Iconic beachfront destination with premium lifestyle amenities',
      highlights: ['Beach Living', 'Marina Views', 'High Demand'],
    },
    {
      id: 'dubai-land',
      name: 'Dubai Land',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776805391/DUBAI_LAND_y3vvy6.png',
      description: 'Massive mixed-use development offering diverse investment options',
      highlights: ['Mixed-Use', 'Entertainment', 'Affordable Luxury'],
    },
  ];

  return (
    <section id="areas" className="relative py-24 overflow-hidden bg-gradient-to-br from-[#FFF8DC] to-[#F5F5DC]">
      {/* Animated background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/4 right-0 w-96 h-96 bg-yellow-50 rounded-full blur-3xl opacity-40"
          animate={{
            scale: [1, 1.4, 1],
            x: [-50, 50, -50],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 border border-yellow-500/30 rounded-full mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <MapPin className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700 font-semibold">Prime Locations</span>
          </motion.div>
          
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">Discover Dubai's</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Wealth Hotspots
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Strategic areas handpicked for maximum investment returns and lifestyle excellence
          </p>
        </motion.div>

        {/* Areas grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {areas.map((area, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
            >
              <Link to={`/area/${area.id}`}>
                <motion.div
                  className="relative h-[500px] rounded-3xl overflow-hidden cursor-pointer bg-white shadow-lg"
                  whileHover={{ scale: 1.03, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Image */}
                  <img
                    src={area.image}
                    alt={area.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Gradient overlay - white to golden */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-yellow-50/40 to-transparent" />

                  {/* Content */}
                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    {/* Area name */}
                    <motion.div
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.15 + 0.3 }}
                    >
                      <h3 className="text-3xl font-bold text-gray-800 mb-3 relative inline-block">
                        {area.name}
                        <motion.div
                          className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600"
                          initial={{ width: 0 }}
                          whileInView={{ width: '100%' }}
                          transition={{ delay: index * 0.15 + 0.5, duration: 0.6 }}
                        />
                      </h3>
                    </motion.div>

                    <p className="text-gray-900 mb-4 text-sm leading-relaxed font-semibold">
                      {area.description}
                    </p>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {area.highlights.map((highlight, idx) => (
                        <motion.span
                          key={idx}
                          className="px-3 py-1 bg-yellow-400/90 border border-yellow-500 rounded-full text-xs text-gray-900 font-medium"
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          transition={{ delay: index * 0.15 + 0.6 + idx * 0.1 }}
                          whileHover={{ scale: 1.1 }}
                        >
                          {highlight}
                        </motion.span>
                      ))}
                    </div>

                    {/* Explore button */}
                    <motion.div
                      className="flex items-center gap-2 text-yellow-400 font-semibold group-hover:gap-4 transition-all duration-300"
                      whileHover={{ x: 5 }}
                    >
                      <span>Explore Opportunities</span>
                      <TrendingUp className="w-5 h-5" />
                    </motion.div>
                  </div>

                  {/* Hover glow effect */}
                  <motion.div
                    className="absolute inset-0 border-2 border-yellow-400/0 group-hover:border-yellow-400/60 rounded-3xl transition-all duration-500"
                  />

                  {/* Sparkle effect on hover */}
                  <motion.div
                    className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    animate={{
                      rotate: [0, 180, 360],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <Sparkles className="w-8 h-8 text-yellow-400" />
                  </motion.div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <motion.a
            href="#contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.98 }}
          >
            <Building className="w-6 h-6" />
            <span>Schedule a Consultation</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default AreasSection;
